// src/components/publishing/brand-publisher-page.tsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Sparkles, ClipboardPaste, Rocket, FileText, Loader2, Trash2, ChevronRight,
  PanelLeft, X,
} from "lucide-react";
import { toast } from "sonner";
import {
  publishingApi, Draft, Integration, Destination, PerDestinationContent,
  PublishResponse, destinationLabel,
} from "@/app/services/publishing/publishing-api";
import { ConfirmDialog } from "../ui/confirm-dialog";
import { useResponsive } from "../../hooks/common/use-breakpoint";

import { AvailableServersStrip } from "./available-servers-strip";
import { DraftComposer } from "./draft-composer";
import { createBrief } from "@/api/content";
import { PasteContentComposer } from "./paste-content-composer";
import { DraftPreviewTabs } from "./draft-preview-tabs";
import { PublishConfirmCard, PublishResultsCard } from "./publish-confirm-card";

interface BrandPublisherPageProps {
  /** Currently active brand. Publishing is scoped per-brand. */
  activeBrandName?: string;
  /**
   * Parent router. Called with a page name (e.g. "integrations") when the
   * user clicks a deep-link CTA like "Go to Integrations" or the "Manage"
   * button in the Available Servers strip.
   */
  onNavigate?: (page: string) => void;
}

type ComposerMode = "ai" | "paste";

export function BrandPublisherPage({
  activeBrandName, onNavigate,
}: BrandPublisherPageProps) {
  // Internal alias — all the existing logic below uses `brandName`.
  const brandName = activeBrandName;
  // Deep-link to Integrations page.
  const onNavigateToIntegrations = onNavigate
    ? () => onNavigate("integrations")
    : undefined;

  const { isDesktop } = useResponsive();

  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(true);

  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [loadingDrafts, setLoadingDrafts] = useState(true);

  const [activeDraftId, setActiveDraftId] = useState<number | null>(null);
  const [activeDraft, setActiveDraft] = useState<Draft | null>(null);

  const [composerMode, setComposerMode] = useState<ComposerMode>("ai");
  const [composerBusy, setComposerBusy] = useState(false);

  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [lastPublishResult, setLastPublishResult] = useState<PublishResponse | null>(null);

  const [draftToDelete, setDraftToDelete] = useState<Draft | null>(null);
  const [rail, setRail] = useState(false); // off-canvas drawer on mobile

  const refreshIntegrations = useCallback(async () => {
    setLoadingIntegrations(true);
    try {
      const list = await publishingApi.listIntegrations(brandName);
      setIntegrations(list);
    } catch (e: any) {
      toast.error(e.message || "Could not load integrations");
    } finally {
      setLoadingIntegrations(false);
    }
  }, [brandName]);

  const refreshDrafts = useCallback(async () => {
    setLoadingDrafts(true);
    try {
      const list = await publishingApi.listDrafts(brandName);
      setDrafts(list);
    } catch (e: any) {
      toast.error(e.message || "Could not load drafts");
    } finally {
      setLoadingDrafts(false);
    }
  }, [brandName]);

  useEffect(() => {
    refreshIntegrations();
    refreshDrafts();
    setActiveDraftId(null);
    setActiveDraft(null);
    setLastPublishResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandName]);

  useEffect(() => {
    if (activeDraftId === null) {
      setActiveDraft(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const d = await publishingApi.getDraft(activeDraftId);
        if (!cancelled) setActiveDraft(d);
      } catch (e: any) {
        if (!cancelled) toast.error(e.message || "Could not load draft");
      }
    })();
    return () => { cancelled = true; };
  }, [activeDraftId]);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleGenerateDraft = async (params: {
    brief: string;
    destinations: Destination[];
    tone?: string;
    actionId?: number | null;
    customerId?: number | null;
  }) => {
    setComposerBusy(true);
    try {
      // #132: an action's brief from the brand record; without one the
      // server makes a free-text brief when the brand has a record.
      const brief = params.actionId != null && params.customerId != null
        ? await createBrief(params.customerId, { action_id: params.actionId })
        : null;
      const created = await publishingApi.createDraft({
        brand_name: brandName,
        brief: params.brief,
        destinations: params.destinations,
        tone: params.tone,
        brief_id: brief?.id,
      });
      toast.success("Draft ready");
      await refreshDrafts();
      setActiveDraftId(created.id);
      setLastPublishResult(null);
    } catch (e: any) {
      toast.error(e.message || "Drafting failed");
    } finally {
      setComposerBusy(false);
    }
  };

  const handleCreateManualDraft = async (params: {
    destinations: Destination[];
    per_destination_content: PerDestinationContent;
    title_hint?: string;
  }) => {
    setComposerBusy(true);
    try {
      const created = await publishingApi.createManualDraft({
        brand_name: brandName,
        destinations: params.destinations,
        per_destination_content: params.per_destination_content,
        title_hint: params.title_hint,
      });
      toast.success("Draft saved");
      await refreshDrafts();
      setActiveDraftId(created.id);
      setLastPublishResult(null);
    } catch (e: any) {
      toast.error(e.message || "Could not save draft");
    } finally {
      setComposerBusy(false);
    }
  };

  const handleDeleteDraft = async () => {
    const d = draftToDelete;
    if (!d) return;
    try {
      await publishingApi.deleteDraft(d.id);
      toast.success("Draft deleted");
      if (activeDraftId === d.id) setActiveDraftId(null);
      await refreshDrafts();
    } catch (e: any) {
      toast.error(e.message || "Delete failed");
    } finally {
      setDraftToDelete(null);
    }
  };

  const handlePublished = (result: PublishResponse) => {
    setLastPublishResult(result);
    setPublishConfirmOpen(false);
    // Refresh draft to pick up status changes
    if (activeDraftId) {
      publishingApi.getDraft(activeDraftId).then(d => setActiveDraft(d)).catch(() => {});
    }
    refreshDrafts();
  };

  // ─── Derived ───────────────────────────────────────────────────────────────

  const hasAnyConnected = integrations.some(i => i.status === "active");

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{
      minHeight: "100%", display: "flex",
      backgroundColor: "var(--background)",
    }}>
      {/* Left rail — drafts list */}
      {isDesktop ? (
        <LeftRail
          drafts={drafts}
          loading={loadingDrafts}
          activeDraftId={activeDraftId}
          onSelect={(id) => { setActiveDraftId(id); setLastPublishResult(null); }}
          onDelete={setDraftToDelete}
          onNew={() => { setActiveDraftId(null); setLastPublishResult(null); }}
        />
      ) : (
        rail && (
          <MobileDrawer onClose={() => setRail(false)}>
            <LeftRail
              drafts={drafts}
              loading={loadingDrafts}
              activeDraftId={activeDraftId}
              onSelect={(id) => { setActiveDraftId(id); setLastPublishResult(null); setRail(false); }}
              onDelete={(d) => { setDraftToDelete(d); setRail(false); }}
              onNew={() => { setActiveDraftId(null); setLastPublishResult(null); setRail(false); }}
            />
          </MobileDrawer>
        )
      )}

      {/* Main column */}
      <div style={{
        flex: 1, minWidth: 0,
        display: "flex", flexDirection: "column",
      }}>
        <PageHeader
          brandName={brandName}
          isDesktop={isDesktop}
          onOpenRail={() => setRail(true)}
        />

        <div style={{
          flex: 1,
          padding: "16px 20px 40px",
          maxWidth: 960, width: "100%", margin: "0 auto",
          display: "flex", flexDirection: "column", gap: 16,
        }}>
          {/* Available servers strip */}
          <AvailableServersStrip
            integrations={integrations.filter(i => i.status === "active")}
            loading={loadingIntegrations}
            onManageClick={onNavigateToIntegrations}
          />

          {/* Composer OR Draft preview */}
          {activeDraft === null ? (
            <ComposerCard>
              <ModeToggle mode={composerMode} onChange={setComposerMode} disabled={composerBusy} />

              {composerMode === "ai" && (
                <DraftComposer
                  integrations={integrations}
                  brandName={brandName}
                  onDraft={handleGenerateDraft}
                  drafting={composerBusy}
                  onNavigateToIntegrations={onNavigateToIntegrations}
                />
              )}

              {composerMode === "paste" && (
                <PasteContentComposer
                  integrations={integrations}
                  brandName={brandName}
                  onCreate={handleCreateManualDraft}
                  creating={composerBusy}
                />
              )}
            </ComposerCard>
          ) : (
            <>
              <DraftHeader
                draft={activeDraft}
                onBack={() => { setActiveDraftId(null); setLastPublishResult(null); }}
                onDelete={() => setDraftToDelete(activeDraft)}
              />

              <ComposerCard>
                <DraftPreviewTabs
                  draft={activeDraft}
                  onUpdated={setActiveDraft}
                />
              </ComposerCard>

              {lastPublishResult ? (
                <PublishResultsCard
                  result={lastPublishResult}
                  onDismiss={() => setLastPublishResult(null)}
                />
              ) : publishConfirmOpen ? (
                <PublishConfirmCard
                  draft={activeDraft}
                  integrations={integrations}
                  onCancel={() => setPublishConfirmOpen(false)}
                  onPublished={handlePublished}
                />
              ) : (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button
                    onClick={() => setPublishConfirmOpen(true)}
                    disabled={!hasAnyConnected}
                    title={!hasAnyConnected ? "Connect at least one destination first" : undefined}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 7,
                      padding: "10px 18px", borderRadius: 9,
                      border: "none",
                      backgroundColor: hasAnyConnected ? "#10B981" : "var(--border)",
                      color: "white",
                      cursor: hasAnyConnected ? "pointer" : "not-allowed",
                      fontSize: 13, fontWeight: 600,
                    }}
                  >
                    <Rocket style={{ width: 13, height: 13 }} />
                    Review & publish
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={draftToDelete !== null}
        onOpenChange={(open) => { if (!open) setDraftToDelete(null); }}
        onConfirm={handleDeleteDraft}
        title={draftToDelete ? "Delete this draft?" : "Delete?"}
        description="This removes the draft permanently. Any posts already published will stay published."
        confirmLabel="Delete"
        destructive
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Sub-components
// ═══════════════════════════════════════════════════════════════════════════

function PageHeader({
  brandName, isDesktop, onOpenRail,
}: {
  brandName?: string;
  isDesktop: boolean;
  onOpenRail: () => void;
}) {
  return (
    <div style={{
      padding: "18px 22px 14px",
      borderBottom: "1px solid var(--border)",
      backgroundColor: "var(--background)",
      position: "sticky", top: 0, zIndex: 5,
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        maxWidth: 960, margin: "0 auto",
      }}>
        {!isDesktop && (
          <button
            onClick={onOpenRail}
            aria-label="Open drafts"
            style={{
              padding: 7, borderRadius: 7,
              border: "1px solid var(--border)",
              backgroundColor: "var(--muted)",
              cursor: "pointer", color: "var(--muted-foreground)",
            }}
          >
            <PanelLeft style={{ width: 14, height: 14 }} />
          </button>
        )}
        <div
          style={{
            width: 32, height: 32, borderRadius: 8,
            backgroundColor: "rgba(13,148,136,0.1)",
            border: "1px solid rgba(13,148,136,0.2)",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Rocket style={{ width: 16, height: 16, color: "#0D9488" }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{
            fontSize: 18, fontWeight: 700, color: "var(--foreground)", margin: 0,
          }}>
            Brand Publisher
          </h1>
          <p style={{
            fontSize: 12, color: "var(--muted-foreground)", margin: 0,
          }}>
            Publish AI-drafted or pasted content to your connected destinations
            {brandName ? ` · ${brandName}` : ""}
          </p>
        </div>
      </div>
    </div>
  );
}

function ComposerCard({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: 18, borderRadius: 12,
      border: "1px solid var(--border)",
      backgroundColor: "var(--background)",
    }}>
      {children}
    </div>
  );
}

function ModeToggle({
  mode, onChange, disabled,
}: {
  mode: ComposerMode;
  onChange: (m: ComposerMode) => void;
  disabled: boolean;
}) {
  return (
    <div style={{
      display: "flex", gap: 4, padding: 3, borderRadius: 8,
      backgroundColor: "var(--muted)",
      border: "1px solid var(--border)",
      marginBottom: 14,
      width: "fit-content",
    }}>
      <ModeButton
        active={mode === "ai"}
        onClick={() => onChange("ai")}
        disabled={disabled}
        icon={<Sparkles style={{ width: 12, height: 12 }} />}
        label="Generate with AI"
      />
      <ModeButton
        active={mode === "paste"}
        onClick={() => onChange("paste")}
        disabled={disabled}
        icon={<ClipboardPaste style={{ width: 12, height: 12 }} />}
        label="Paste existing content"
      />
    </div>
  );
}

function ModeButton({
  active, onClick, disabled, icon, label,
}: {
  active: boolean;
  onClick: () => void;
  disabled: boolean;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 12px", borderRadius: 6,
        border: "none",
        backgroundColor: active ? "var(--background)" : "transparent",
        color: active ? "var(--foreground)" : "var(--muted-foreground)",
        boxShadow: active ? "0 1px 2px rgba(0,0,0,0.04)" : "none",
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: 12, fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {icon}
      {label}
    </button>
  );
}

// ─── Drafts rail ─────────────────────────────────────────────────────────────

function LeftRail({
  drafts, loading, activeDraftId, onSelect, onDelete, onNew,
}: {
  drafts: Draft[];
  loading: boolean;
  activeDraftId: number | null;
  onSelect: (id: number) => void;
  onDelete: (d: Draft) => void;
  onNew: () => void;
}) {
  return (
    <div style={{
      width: 320, flexShrink: 0,
      borderRight: "1px solid var(--border)",
      backgroundColor: "var(--muted)",
      display: "flex", flexDirection: "column",
      minHeight: "100%",
    }}>
      <div style={{
        padding: "18px 16px 12px",
        borderBottom: "1px solid var(--border)",
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 8,
        }}>
          <h2 style={{
            fontSize: 13, fontWeight: 700, color: "var(--foreground)", margin: 0,
            textTransform: "uppercase", letterSpacing: "0.05em",
          }}>
            Drafts
          </h2>
          <span style={{
            fontSize: 11, color: "var(--muted-foreground)",
            padding: "1px 7px", borderRadius: 999,
            backgroundColor: "var(--background)",
            border: "1px solid var(--border)",
          }}>
            {drafts.length}
          </span>
        </div>
        <button
          onClick={onNew}
          style={{
            width: "100%",
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            padding: "8px 12px", borderRadius: 8,
            border: "1px solid rgba(13,148,136,0.25)",
            backgroundColor: activeDraftId === null ? "rgba(13,148,136,0.12)" : "rgba(13,148,136,0.06)",
            color: "#0D9488",
            cursor: "pointer", fontSize: 12, fontWeight: 600,
          }}
        >
          <Sparkles style={{ width: 12, height: 12 }} />
          New draft
        </button>
      </div>

      <div style={{
        flex: 1, overflowY: "auto",
        padding: "10px 8px",
      }}>
        {loading && drafts.length === 0 && (
          <div style={{
            padding: 24, textAlign: "center",
            color: "var(--muted-foreground)", fontSize: 12,
          }}>
            <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
          </div>
        )}

        {!loading && drafts.length === 0 && (
          <div style={{
            padding: "20px 12px", textAlign: "center",
            color: "var(--muted-foreground)", fontSize: 12,
          }}>
            No drafts yet. Generate or paste content to get started.
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {drafts.map(d => (
            <DraftListItem
              key={d.id}
              draft={d}
              active={d.id === activeDraftId}
              onClick={() => onSelect(d.id)}
              onDelete={() => onDelete(d)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function DraftListItem({
  draft, active, onClick, onDelete,
}: {
  draft: Draft;
  active: boolean;
  onClick: () => void;
  onDelete: () => void;
}) {
  const isManual = !draft.model_used;

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      style={{
        padding: "10px 10px",
        borderRadius: 8,
        cursor: "pointer",
        backgroundColor: active ? "var(--background)" : "transparent",
        border: `1px solid ${active ? "rgba(13,148,136,0.3)" : "transparent"}`,
        display: "flex", flexDirection: "column", gap: 4,
        position: "relative",
      }}
    >
      <div style={{
        display: "flex", alignItems: "center", gap: 6,
      }}>
        {isManual
          ? <ClipboardPaste style={{ width: 11, height: 11, color: "var(--muted-foreground)", flexShrink: 0 }} />
          : <Sparkles style={{ width: 11, height: 11, color: "#0D9488", flexShrink: 0 }} />
        }
        <span style={{
          fontSize: 10, fontWeight: 700, color: "var(--muted-foreground)",
          textTransform: "uppercase", letterSpacing: "0.04em",
        }}>
          {statusLabel(draft.status)}
        </span>
        <button
          onClick={e => { e.stopPropagation(); onDelete(); }}
          aria-label="Delete draft"
          style={{
            marginLeft: "auto",
            padding: 3, borderRadius: 4,
            border: "none", backgroundColor: "transparent",
            cursor: "pointer", color: "var(--muted-foreground)",
          }}
        >
          <Trash2 style={{ width: 12, height: 12 }} />
        </button>
      </div>
      <div style={{
        fontSize: 13, fontWeight: 600, color: "var(--foreground)",
        lineHeight: 1.4,
        overflow: "hidden", display: "-webkit-box",
        WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
      }}>
        {draft.brief || "(untitled)"}
      </div>
      <div style={{
        fontSize: 10, color: "var(--muted-foreground)",
        display: "flex", gap: 4, flexWrap: "wrap",
      }}>
        {draft.destinations.map(d => (
          <span
            key={d}
            style={{
              padding: "1px 6px", borderRadius: 999,
              backgroundColor: "var(--muted)",
              border: "1px solid var(--border)",
              fontWeight: 600,
            }}
          >
            {destinationLabel(d as Destination)}
          </span>
        ))}
      </div>
    </div>
  );
}

function DraftHeader({
  draft, onBack, onDelete,
}: {
  draft: Draft;
  onBack: () => void;
  onDelete: () => void;
}) {
  const isManual = !draft.model_used;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
    }}>
      <button
        onClick={onBack}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          padding: "7px 11px", borderRadius: 7,
          border: "1px solid var(--border)",
          backgroundColor: "var(--background)",
          color: "var(--foreground)",
          cursor: "pointer", fontSize: 12, fontWeight: 600,
        }}
      >
        ← New draft
      </button>
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "5px 9px", borderRadius: 999,
        backgroundColor: isManual ? "var(--muted)" : "rgba(13,148,136,0.08)",
        border: `1px solid ${isManual ? "var(--border)" : "rgba(13,148,136,0.25)"}`,
        color: isManual ? "var(--muted-foreground)" : "#0D9488",
        fontSize: 10, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.04em",
      }}>
        {isManual
          ? <><ClipboardPaste style={{ width: 10, height: 10 }} /> Pasted</>
          : <><Sparkles style={{ width: 10, height: 10 }} /> AI-drafted</>
        }
      </div>
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "5px 9px", borderRadius: 999,
        backgroundColor: "var(--muted)",
        border: "1px solid var(--border)",
        color: "var(--muted-foreground)",
        fontSize: 10, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.04em",
      }}>
        {statusLabel(draft.status)}
      </div>
      <div style={{ flex: 1 }} />
      <button
        onClick={onDelete}
        aria-label="Delete draft"
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          padding: "7px 11px", borderRadius: 7,
          border: "1px solid var(--border)",
          backgroundColor: "var(--background)",
          color: "var(--muted-foreground)",
          cursor: "pointer", fontSize: 12, fontWeight: 600,
        }}
      >
        <Trash2 style={{ width: 12, height: 12 }} />
        Delete
      </button>
    </div>
  );
}

function MobileDrawer({
  children, onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 40,
          backgroundColor: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(4px)",
        }}
      />
      <div style={{
        position: "fixed", left: 0, top: 0, bottom: 0, zIndex: 50,
        width: "min(340px, 100vw)",
        display: "flex", flexDirection: "column",
        animation: "slideInLeft 0.25s ease-out",
      }}>
        <div style={{
          position: "absolute", top: 12, right: 12, zIndex: 2,
        }}>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              padding: 6, borderRadius: 7,
              border: "1px solid var(--border)",
              backgroundColor: "var(--background)",
              cursor: "pointer", color: "var(--muted-foreground)",
            }}
          >
            <X style={{ width: 14, height: 14 }} />
          </button>
        </div>
        {children}
      </div>
      <style>{`
        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}

function statusLabel(s: string): string {
  return {
    pending: "Draft",
    approved: "Approved",
    published: "Published",
    partially_published: "Partial",
    failed: "Failed",
    cancelled: "Cancelled",
  }[s] || s;
}